import React, { useEffect, useState } from 'react';

import { SplitContentBlockData } from 'api/cms';
import Box from 'components/Box';
import { PlayButton } from 'components/Video';
import { bp, colors, gutterWidth } from 'theme';

import { Content } from './Content';

export function SplitContentBlock({
  blockId,
  data: { sections, appearance },
  blockIndex,
  pageUpdated,
  preview,
}: {
  blockId: string;
  data: SplitContentBlockData;
  blockIndex: number;
  pageUpdated: string;
  preview?: boolean;
}) {
  const [isDesktop, setIsDesktop] = useState(true);
  useEffect(() => {
    const resizeHandler = () =>
      setIsDesktop(window && window.innerWidth >= bp[1]);

    window.addEventListener('resize', resizeHandler);
    resizeHandler();
    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, []);
  const actualAppearance = appearance;
  const flipOnMobile =
    ['TEXT', 'BLOCKQUOTE'].includes(sections[0].type) &&
    ['IMAGE', 'VIDEO'].includes(sections[1].type);
  return (
    <Box
      data-qa-id={`split-content-block-${blockIndex}`}
      py={actualAppearance !== 'SIDE_BY_SIDE' && gutterWidth}
    >
      <Box
        position="relative"
        display="flex"
        flexDirection={[
          flipOnMobile ? 'column-reverse' : 'column',
          null,
          'row',
        ]}
        alignItems={actualAppearance === 'FULL_WIDTH' && 'center'}
        m={[-gutterWidth / 4, null, -gutterWidth / 2]}
        width={[
          `calc(100% + ${gutterWidth / 2}px)`,
          null,
          `calc(100% + ${gutterWidth}px)`,
        ]}
        minHeight={actualAppearance === 'FULL_WIDTH' && 'calc(100vh - 300px)'}
      >
        {sections.map((section, index) => {
          const overlapping =
            actualAppearance === 'OVERLAPPING' &&
            (section.type === 'IMAGE' || section.type === 'VIDEO');
          const fullWidth =
            actualAppearance === 'FULL_WIDTH' &&
            (section.type === 'IMAGE' || section.type === 'VIDEO') &&
            isDesktop;
          const showTextBox =
            ['FULL_WIDTH', 'OVERLAPPING'].includes(actualAppearance) &&
            ['TEXT', 'BLOCKQUOTE'].includes(section.type);
          return (
            <Box
              data-qa-id={`split-content-section-${index}`}
              position={!fullWidth ? 'relative' : undefined}
              zIndex={showTextBox && 1}
              width={[1, null, 1 / 2]}
              p={[gutterWidth / 4, null, gutterWidth / 2]} // eslint-disable-next-line react/no-array-index-key
              key={`${section.type}-${index}`}
            >
              <Box
                position={[
                  null,
                  null,
                  fullWidth && !preview ? 'absolute' : undefined,
                ]}
                left={[null, null, '50%']}
                top={[null, null, '50%']}
                transform={[
                  null,
                  null,
                  fullWidth && !preview && 'translate(-50%, -50%)',
                ]}
                height={
                  section.type === 'IMAGE' &&
                  section.data.size === 'FIXED_WIDTH'
                    ? 'auto'
                    : [
                        '100%',
                        null,
                        overlapping || fullWidth
                          ? `calc(100% + ${
                              overlapping ? gutterWidth * 2 : gutterWidth
                            }px)`
                          : '100%',
                      ]
                }
                mt={overlapping && [0, null, -gutterWidth]}
                mb={overlapping && [-gutterWidth, null, 0]}
                width={
                  // TODO: Fix this the next time the file is edited.
                  // eslint-disable-next-line no-nested-ternary
                  overlapping
                    ? [1, null, `calc(100% + ${gutterWidth * 3}px)`]
                    : fullWidth
                    ? [1, null, '100vw']
                    : 1
                }
                ml={[0, null, overlapping && index === 1 && -gutterWidth * 3]}
                overflow={
                  ['TEXT', 'BLOCKQUOTE'].includes(section.type)
                    ? undefined
                    : 'hidden'
                }
                bg={
                  section.type === 'TEXT' || showTextBox ? 'white' : colors.fog
                }
                boxShadow={showTextBox && '0 0 6px rgba(0,0,0,0.3)'}
                borderRadius={[showTextBox ? 12 : 24, null, fullWidth && 0]}
                style={
                  section.type === 'IMAGE' && {
                    '-webkit-mask-image':
                      '-webkit-radial-gradient(white, black)',
                  }
                }
              >
                <>
                  <Content
                    section={section}
                    showTextBox={showTextBox}
                    fullWidth={fullWidth}
                    appearance={actualAppearance}
                    index={index}
                    blockId={blockId}
                    blockIndex={blockIndex}
                    pageUpdated={pageUpdated}
                    isDesktop={isDesktop}
                  />

                  {fullWidth && section.type === 'VIDEO' && (
                    <Box
                      position="absolute"
                      top="50%"
                      left={['50%', null, index === 0 ? '25%' : '75%']}
                      transform="translate(-50%, -50%)"
                      pointerEvents="none"
                    >
                      <PlayButton />
                    </Box>
                  )}
                </>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
